import Button from '@/common/components/base/button/Button';
import {
  MapModalType,
  MapModalWrapperType,
} from '@/common/components/base/input/mapInput/modals/map/MapModal.Type';
import { CurrentPositionType } from '@/common/components/base/map/map.type';
import MainModal from '@/common/components/base/modal/Modal';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import { mapSearchLocationFunction } from '@/common/queries/mapSearchLocation/mapSearchLocationQueryFunction';
import { useLoadingStore } from '@/common/store/mapLoading/useLoadingOfMap';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { css } from 'styled-system/css';
import { Stack } from 'styled-system/jsx';
import { SanaapMap } from '@sanaap/sanaap-map';
import '@sanaap/sanaap-map/style.css';
import 'maplibre-gl/dist/maplibre-gl.css';
import LoadingPage from '@/common/pages/LoadingPage';

const getAddressByLatLng = async (lat: number, lng: number) => {
  const result = await mapSearchLocationFunction({ lat, lng });
  return {
    address: result.address,
    province: result.province,
    county: result.county,
  };
};

const MapModalWrapper = ({
  open,
  closeModal,
  title,
  onSubmit,
  location,
  setLocation,
  onAddressFetched,
  initialAddress,
}: MapModalWrapperType) => {
  const { loading } = useLoadingStore();

  return (
    <MainModal
      open={open}
      closeModal={() => (loading ? undefined : closeModal())}
      childrenWrapperClassName={css({ width: '100%' })}
    >
      <MapModal
        title={title}
        setLocation={setLocation}
        location={location}
        onSubmit={onSubmit}
        closeModal={closeModal}
        onAddressFetched={onAddressFetched}
        isLoading={loading}
        initialAddress={initialAddress}
      />
    </MainModal>
  );
};

const MapModal = ({
  title,
  location,
  setLocation,
  onAddressFetched,
  onSubmit,
  isLoading,
  initialAddress,
}: MapModalType) => {
  const mapWrapperRef = useRef<HTMLDivElement>(null);
  const [moveEndLocation, setMoveEndLocation] =
    useState<CurrentPositionType | null>(null);

  const { control, setValue } = useFormContext();
  const { address } = useWatch({ control });

  const setMapSearchInputValue = (value: string) => {
    const searchInput =
      mapWrapperRef.current?.querySelector<HTMLTextAreaElement>(
        'textarea[aria-label="Search locations"]',
      );
    if (searchInput) {
      const valueSetter = Object.getOwnPropertyDescriptor(
        HTMLTextAreaElement.prototype,
        'value',
      )?.set;
      valueSetter?.call(searchInput, value);
      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  useEffect(() => {
    if (initialAddress) {
      setValue('address', {
        label: initialAddress,
        value: {
          province: undefined,
          county: undefined,
          lat: location?.lat,
          lng: location?.lng,
        },
      });
      setMapSearchInputValue(initialAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMapMoveEnd = useCallback((data: CurrentPositionType) => {
    setMoveEndLocation(data);
  }, []);

  const handleAddressFetched = useCallback(
    (
      response: {
        address?: string;
        province?: string;
        county?: string;
      },
      coordinates: CurrentPositionType,
    ) => {
      handleMapMoveEnd(coordinates);
      if (response.address) {
        setMapSearchInputValue(response.address);
      }
      setValue('address', {
        label: response.address ?? '',
        value: {
          province: response.province,
          county: response.county,
          lat: coordinates.lat,
          lng: coordinates.lng,
        },
      });
      onAddressFetched?.(response, coordinates);
    },
    [handleMapMoveEnd, onAddressFetched, setValue],
  );

  const handleSubmit = () => {
    if (moveEndLocation) {
      setLocation(moveEndLocation);
    }
    onSubmit();
  };

  return (
    <Stack h="75dvh" mt="1rem">
      <TitleDescription mx="20px">{title}</TitleDescription>
      <Stack
        ref={mapWrapperRef}
        position="relative"
        flex="1"
        overflow="hidden"
        className={css({
          '& > div': { width: '100% !important', height: '100% !important' },
        })}
      >
        <SanaapMap
          data={[]}
          tokens={{
            accessToken:
              (window as any)?.env?.VITE_MAP_ACCESS_TOKEN ||
              import.meta.env.VITE_MAP_ACCESS_TOKEN,
            stylesBaseUrl:
              (window as any)?.env?.VITE_MAP_STYLES_BASE_URL ||
              import.meta.env.VITE_MAP_STYLES_BASE_URL,
          }}
          search={{ show: true }}
          theme={{ showSelector: true }}
          reverseGeocode={{
            fetchAddressOnClick: true,
            syncInputWithAddress: true,
            getAddressByLatLng,
            onAddressFetched: handleAddressFetched,
            showClickPinMarker: true,
          }}
          geolocate={{
            triggerOnMount: true,
            showLocateButton: false,
          }}
          loading={{ component: <LoadingPage /> }}
          showNavigationControl={false}
          onLocationError={(error: Error | GeolocationPositionError) => {
            console.error('[SanaapMap] reverse geocode error', error);
          }}
        />
      </Stack>
      <Button
        type="button"
        onClick={handleSubmit}
        mx="20px"
        disabled={!address || isLoading}
      >
        ثبت
      </Button>
    </Stack>
  );
};

export default MapModalWrapper;
